import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { ArrowForwardRounded } from '@mui/icons-material';
import type { PublicPath } from '../../../lib/public-path';
import { programHref } from '../../../lib/public-path';
import { LANDING } from '../landing-theme';

/** «Año 1», «Años 2 y 3», «Años 2 a 4»: cómo se nombran los años de un programa. */
export function yearsLabel(t: TFunction, years: number[]): string {
  if (years.length === 0) return '';
  const a = years[0]!;
  const b = years[years.length - 1]!;
  if (years.length === 1) return t('publicLanding.path.years_single', { a });
  if (years.length === 2) return t('publicLanding.path.years_pair', { a, b });
  return t('publicLanding.path.years_range', { a, b });
}

/**
 * La ruta de formación en el landing: cada programa es un escalón que sube un
 * año tras otro, y cada escalón lleva a la página del programa. La escalera la
 * calcula `buildPublicPath` con la misma regla que el módulo académico.
 */
export function ProgramPath({
  path,
  basePath,
  gold,
  goldAlpha,
}: {
  path: PublicPath;
  basePath: string;
  gold: string;
  goldAlpha: (a: number) => string;
}) {
  const { t } = useTranslation('sis');
  const { SERIF, SANS, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_MUTED, BORDER, RULE, RAISED, BG } =
    LANDING;
  const cols = path.columns.length;
  const inYears = path.columns.every((c) => c.year !== null);
  const top = [...path.rungs].sort((a, b) => b.startColumn - a.startColumn)[0]!;

  return (
    <section
      id="programas"
      className="rp"
      style={{ background: BG, borderTop: `1px solid ${BORDER}` }}
    >
      <style>{`
        .rp { background-image: radial-gradient(60% 50% at 85% 0%, ${goldAlpha(0.07)}, transparent 70%); }
        .rp-wrap { max-width: 1200px; margin: 0 auto; padding: 96px 64px 80px; }
        .rp-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 32px; flex-wrap: wrap; }
        .rp-title { font-family: ${SERIF}; font-weight: 400; font-size: clamp(2rem, 4vw, 3rem); line-height: 1.12; color: ${TEXT_PRIMARY}; margin: 0; text-wrap: balance; }
        .rp-lede { font-family: ${SANS}; color: ${TEXT_SECONDARY}; font-size: 16px; line-height: 1.65; max-width: 60ch; margin: 16px 0 0; }
        .rp-total { text-align: right; }
        .rp-total b { display: block; font-family: ${SERIF}; font-weight: 500; font-size: 56px; line-height: 1; color: ${TEXT_PRIMARY}; font-variant-numeric: lining-nums; }
        .rp-total span { font-family: ${SANS}; font-size: 11px; letter-spacing: .16em; text-transform: uppercase; color: ${TEXT_MUTED}; }
        .rp-stair { margin-top: 48px; display: grid; grid-template-columns: repeat(${cols}, minmax(0, 1fr)); column-gap: 10px; row-gap: 10px; }
        .rp-grid { border-left: 1px dashed rgba(232,228,220,.06); pointer-events: none; }
        .rp-yr { padding-top: 14px; border-top: 1px solid ${RULE}; font-family: ${SANS}; font-size: 11px; letter-spacing: .16em; text-transform: uppercase; color: ${TEXT_MUTED}; }
        .rp-yr b { display: block; font-family: ${SERIF}; font-size: 22px; font-weight: 500; letter-spacing: 0; text-transform: none; color: ${TEXT_SECONDARY}; font-variant-numeric: lining-nums; }
        .rp-rung {
          position: relative; display: flex; flex-direction: column; gap: 6px; text-decoration: none;
          border: 1px solid ${RULE}; border-left: 2px solid ${gold}; border-radius: 3px;
          background: linear-gradient(180deg, #171a1a, ${RAISED}); padding: 14px 18px;
          transition: border-color .25s ease, transform .25s ease;
        }
        .rp-rung:hover { border-color: ${goldAlpha(0.45)}; border-left-color: ${gold}; transform: translateY(-2px); }
        .rp-rung:focus-visible { outline: 2px solid ${goldAlpha(0.8)}; outline-offset: 3px; }
        .rp-rung.is-top { background: linear-gradient(180deg, ${goldAlpha(0.16)}, ${goldAlpha(0.05)}); border-color: ${goldAlpha(0.35)}; }
        .rp-code { font-family: ${SANS}; font-size: 10.5px; font-weight: 600; letter-spacing: .2em; text-transform: uppercase; color: ${gold}; padding-right: 22px; }
        .rp-name { font-family: ${SERIF}; font-size: 20px; line-height: 1.12; font-weight: 500; color: ${TEXT_PRIMARY}; text-wrap: balance; }
        .rp-meta { margin-top: auto; display: flex; gap: 4px 12px; flex-wrap: wrap; font-family: ${SANS}; font-size: 12px; color: ${TEXT_SECONDARY}; font-variant-numeric: tabular-nums; }
        .rp-cum { font-family: ${SANS}; font-size: 11.5px; color: ${gold}; }
        .rp-go { position: absolute; right: 12px; top: 12px; color: ${TEXT_MUTED}; font-size: 16px !important; transition: color .2s, transform .2s; }
        .rp-rung:hover .rp-go { color: ${gold}; transform: translateX(3px); }
        @media (max-width: 760px) {
          .rp-wrap { padding: 64px 24px 56px; }
          .rp-total { text-align: left; }
          .rp-stair { grid-template-columns: minmax(0, 1fr); row-gap: 12px; margin-top: 32px; }
          .rp-grid, .rp-yr { display: none; }
          .rp-rung { grid-column: 1 !important; grid-row: auto !important; margin-left: calc(var(--step) * 14px); }
        }
        @media (prefers-reduced-motion: reduce) { .rp-rung, .rp-go { transition: none; } }
      `}</style>
      <div className="rp-wrap">
        <div className="rp-head">
          <div>
            <p className="section-label">{t('publicLanding.path.label')}</p>
            <h2 className="rp-title">
              {t('publicLanding.path.title', { count: path.rungs.length })}
            </h2>
            <p className="rp-lede">{t('publicLanding.path.lede', { top: top.program.name })}</p>
          </div>
          <div className="rp-total">
            <b>{path.totalCredits}</b>
            <span>
              {t('publicLanding.path.totalCredits')}
              {inYears ? ` · ${t('publicLanding.path.totalYears', { count: cols })}` : ''}
            </span>
          </div>
        </div>

        <div className="rp-stair">
          {path.columns.map((_c, i) => (
            <div
              key={`g${i}`}
              className="rp-grid"
              style={{ gridColumn: i + 1, gridRow: `1 / ${path.rungs.length + 1}` }}
            />
          ))}
          {path.rungs.map((r, i) => (
            <Link
              key={r.program.id}
              to={programHref(basePath, r.slug)}
              className={`rp-rung${r.cumulativeCredits !== null ? ' is-top' : ''}`}
              style={
                {
                  gridColumn: `${r.startColumn + 1} / span ${r.span}`,
                  gridRow: path.rungs.length - i,
                  '--step': i,
                } as CSSProperties
              }
              aria-label={t('publicLanding.path.open', { name: r.program.name })}
            >
              <ArrowForwardRounded className="rp-go" />
              <span className="rp-code">
                {r.program.code}
                {r.years.length > 0 ? ` · ${yearsLabel(t, r.years)}` : ''}
              </span>
              <span className="rp-name">{r.program.name}</span>
              <span className="rp-meta">
                <span>
                  {t('publicLanding.path.credits', { count: r.credits })} ·{' '}
                  {t('publicLanding.path.courses', { count: r.courseIds.length })}
                </span>
                <span>
                  {r.requires
                    ? t('publicLanding.path.requires', { code: r.requires.code })
                    : t('publicLanding.path.entry')}
                </span>
              </span>
              {r.cumulativeCredits !== null && (
                <span className="rp-cum">
                  {t('publicLanding.path.cumulative', { count: r.cumulativeCredits })}
                </span>
              )}
            </Link>
          ))}
          {inYears &&
            path.columns.map((c, i) => (
              <div
                key={`y${i}`}
                className="rp-yr"
                style={{ gridColumn: i + 1, gridRow: path.rungs.length + 1 }}
              >
                <b>{c.year}</b>
                {t('publicLanding.path.yearAxis')}
              </div>
            ))}
        </div>
      </div>
    </section>
  );
}
